import { ThemeEvents, SellingPlanChangeEvent } from '@theme/events';

/**
 * System Subscription Widget component.
 * Toggle to enable subscription on the parent system product.
 * When enabled, sets the selling plan on the parent product form
 * and updates variant picker prices to show subscription pricing.
 */
class SystemSubscriptionWidget extends HTMLElement {
  /** @type {Object|null} */
  #variantData = null;

  /** @type {boolean} */
  #isEnabled = false;

  /** @type {string|null} */
  #selectedSellingPlanId = null;

  /** @type {Element|null} */
  #closestSection = null;

  connectedCallback() {
    this.#closestSection = this.closest('.shopify-section, dialog');

    this.#parseVariantData();
    this.#initializeState();
    this.#bindEventHandlers();

    if (this.#closestSection) {
      this.#closestSection.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }

    // Dispatch initial state
    queueMicrotask(() => {
      this.#dispatchSellingPlanChange();
      if (this.#isEnabled) this.#applySubscriptionPrices();
    });
  }

  disconnectedCallback() {
    if (this.#closestSection) {
      this.#closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  #parseVariantData() {
    const dataScript = this.querySelector('script[ref="variantData"]');
    if (dataScript) {
      try {
        this.#variantData = JSON.parse(dataScript.textContent || '{}');
      } catch (e) {
        console.error('SystemSubscriptionWidget: Failed to parse variant data:', e);
      }
    }
  }

  #initializeState() {
    this.#isEnabled = this.dataset.defaultEnabled === 'true';

    const allocations = this.#variantData?.allocations;
    if (this.#isEnabled && allocations?.length > 0) {
      this.#selectedSellingPlanId = String(allocations[0].sellingPlanId);
    } else {
      this.#selectedSellingPlanId = null;
    }
  }

  #bindEventHandlers() {
    const toggle = this.querySelector('[ref="toggleInput"]');
    if (toggle) {
      toggle.addEventListener('change', this.#handleToggleChange);
    }

    const frequencySelect = this.querySelector('[ref="frequencySelect"]');
    if (frequencySelect) {
      frequencySelect.addEventListener('change', this.#handleFrequencyChange);
    }
  }

  /** @param {Event} event */
  #handleToggleChange = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    this.#isEnabled = target.checked;

    const frequencyContainer = this.querySelector('[ref="frequencyContainer"]');
    if (frequencyContainer) {
      frequencyContainer.hidden = !this.#isEnabled;
    }

    if (this.#isEnabled) {
      // Set selling plan from current frequency selection
      const frequencySelect = /** @type {HTMLSelectElement|null} */ (this.querySelector('[ref="frequencySelect"]'));
      if (frequencySelect) {
        this.#selectedSellingPlanId = frequencySelect.value;
      } else if (this.#variantData?.allocations?.length > 0) {
        this.#selectedSellingPlanId = String(this.#variantData.allocations[0].sellingPlanId);
      }
      this.#applySubscriptionPrices();
    } else {
      this.#selectedSellingPlanId = null;
      this.#applyRegularPrices();
    }

    this.#dispatchSellingPlanChange();
  };

  /** @param {Event} event */
  #handleFrequencyChange = (event) => {
    this.#selectedSellingPlanId = /** @type {HTMLSelectElement} */ (event.target).value;
    this.#dispatchSellingPlanChange();
  };

  #handleVariantUpdate = (event) => {
    // Check if this is for our product
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      if (!event.detail?.data?.newProduct) return;
    }

    // Find the new widget in the updated HTML
    const newWidget = event.detail?.data?.html?.querySelector('system-subscription-widget');
    if (!newWidget) return;

    // Preserve toggle state
    const wasEnabled = this.#isEnabled;
    const previousPlanId = this.#selectedSellingPlanId;

    // Update variant ID
    this.dataset.variantId = newWidget.dataset.variantId;

    // Update inner content
    this.innerHTML = newWidget.innerHTML;

    // Re-parse and re-bind
    this.#parseVariantData();
    this.#bindEventHandlers();

    // Restore toggle state
    this.#isEnabled = wasEnabled;
    const toggleInput = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="toggleInput"]'));
    if (toggleInput) {
      toggleInput.checked = wasEnabled;
    }

    const frequencyContainer = this.querySelector('[ref="frequencyContainer"]');
    if (frequencyContainer) {
      frequencyContainer.hidden = !wasEnabled;
    }

    // Restore selling plan if it still exists
    if (wasEnabled) {
      const allocations = this.#variantData?.allocations;
      if (previousPlanId && allocations?.some((a) => String(a.sellingPlanId) === previousPlanId)) {
        this.#selectedSellingPlanId = previousPlanId;
        const frequencySelect = /** @type {HTMLSelectElement|null} */ (this.querySelector('[ref="frequencySelect"]'));
        if (frequencySelect) {
          frequencySelect.value = previousPlanId;
        }
      } else if (allocations?.length > 0) {
        this.#selectedSellingPlanId = String(allocations[0].sellingPlanId);
      }
    } else {
      this.#selectedSellingPlanId = null;
    }

    this.#dispatchSellingPlanChange();

    // Re-apply subscription prices after morph if enabled
    if (this.#isEnabled) {
      requestAnimationFrame(() => this.#applySubscriptionPrices());
    }
  };

  #dispatchSellingPlanChange() {
    let price = this.#variantData?.oneTimePrice || 0;
    let sellingPlanName = null;

    if (this.#isEnabled && this.#selectedSellingPlanId && this.#variantData?.allocations) {
      const allocation = this.#variantData.allocations.find(
        (a) => String(a.sellingPlanId) === this.#selectedSellingPlanId
      );
      if (allocation) {
        price = allocation.perDeliveryPrice || allocation.price;
        sellingPlanName = allocation.sellingPlanName || null;
      }
    }

    const event = new SellingPlanChangeEvent({
      sellingPlanId: this.#isEnabled ? this.#selectedSellingPlanId : null,
      sellingPlanName,
      purchaseType: this.#isEnabled ? 'subscription' : 'one-time',
      price,
      productId: this.dataset.productId,
      variantId: this.dataset.variantId,
    });

    this.dispatchEvent(event);
  }

  /** @returns {NodeListOf<HTMLElement>} */
  #getPriceElements() {
    const picker = this.#closestSection?.querySelector('variant-picker');
    if (!picker) return /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('.noop'));
    return picker.querySelectorAll('.variant-option__price[data-sub-price]');
  }

  #applySubscriptionPrices() {
    for (const el of this.#getPriceElements()) {
      const compareEl = el.querySelector('.variant-option__compare-at-price');
      const saleEl = el.querySelector('.variant-option__sale-price');
      const subCompare = el.dataset.subCompare;
      const subPrice = el.dataset.subPrice;

      if (subCompare) {
        if (compareEl) {
          compareEl.textContent = subCompare;
        } else {
          const newCompare = document.createElement('span');
          newCompare.className = 'variant-option__compare-at-price';
          newCompare.textContent = subCompare;
          el.prepend(newCompare);
        }
      }

      if (saleEl && subPrice) {
        saleEl.textContent = subPrice;
      }
    }
  }

  #applyRegularPrices() {
    for (const el of this.#getPriceElements()) {
      const compareEl = el.querySelector('.variant-option__compare-at-price');
      const saleEl = el.querySelector('.variant-option__sale-price');

      if (el.dataset.regularCompare) {
        if (compareEl) compareEl.textContent = el.dataset.regularCompare;
      } else if (compareEl) {
        compareEl.remove();
      }

      if (saleEl && el.dataset.regularPrice) {
        saleEl.textContent = el.dataset.regularPrice;
      }
    }
  }
}

if (!customElements.get('system-subscription-widget')) {
  customElements.define('system-subscription-widget', SystemSubscriptionWidget);
}
